import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography
} from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import MultiStatusUpdateModal from "./MultiStatusUpdateModal";
import TaskFilters from "../TaskFilters";
import { EventType, StatusCategory, SelectedTasks } from "../types";
import { useSnackbar } from "notistack";
import Iconify from "../../../minimals/components/iconify";
import { Contact } from "../../shared/orderInfo/types";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import axios from "axios";
import NewTaskRow from "./NewTaskRow";
import { Category, NewTask, OmitOption, SortConfig } from "./types";
import { copyTasksToClipboard } from "./newTaskUtils";
import { useSortedTasks } from "./hooks/newUseSortedTasks";
import { useTaskFilters } from "./hooks/newUseTaskFilters";
import AssignmentModal from "./assignment/AssignmentModal";
import TaskNotesModal from "./notes/TaskNotesModal";
import DocumentUploadModal from "./documents/DocumentUploadModal";
import ViewAssociatedDocumentsModal from "./documents/ViewAssociatedDocumentsModal";
import HideColumns from "./HideColumns";
import StickyTable from "../../shared/StickyTable";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";

const TasksTable = ({
  tasks,
  setTasks,
  statusCategories,
  eventTypes,
  refreshTasks,
  orderNumber,
  setSelectedTaskHistory,
  contacts,
  categories,
  isCondensed,
  hideCompleted,
  setHideCompleted
}: {
  tasks: NewTask[];
  setTasks: Dispatch<SetStateAction<NewTask[] | null>>;
  statusCategories: StatusCategory[];
  eventTypes: EventType[];
  refreshTasks: () => Promise<void>;
  orderNumber: string | null;
  setSelectedTaskHistory: Dispatch<SetStateAction<NewTask | null>>;
  contacts: Contact[];
  categories: Category[];
  isCondensed?: boolean;
  hideCompleted: boolean;
  setHideCompleted: Dispatch<SetStateAction<boolean>>;
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedTaskNotes, setSelectedTaskNotes] = useState<NewTask | null>(null);
  const [selectedTaskAssignment, setSelectedTaskAssignment] = useState<NewTask | null>(null);
  const [selectedTaskUploadDocument, setSelectedTaskUploadDocument] = useState<NewTask | null>(null);
  const [selectedTaskViewDocuments, setSelectedTaskViewDocuments] = useState<NewTask | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [selectedTasks, setSelectedTasks] = useState<SelectedTasks>({});
  const { sortedTasks, columns } = useSortedTasks(tasks, sortConfig, categories);
  const [viewComlumnAnchorEl, setViewComlumnAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [statusToUpdateAfterRequiredUpload, setStatusToUpdateAfterRequiredUpload] = useState<number | null>(null);
  const [isMultiStatusModalOpen, setIsMultiStatusModalOpen] = useState(false);

  const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => {
    const stored = localStorage.getItem("hiddenColumns");
    return stored ? JSON.parse(stored) : [];
  });

  const {
    statusFilters,
    setStatusFilters,
    activityFilters,
    setActivityFilters,
    assignedToFilters,
    setAssignedToFilters,
    associationFilters,
    setAssociationFilters,
    taskFilter,
    setTaskFilter,
    taskQuickNoteFilter,
    setTaskQuickNoteFilter,
    associationFilterOptions,
    assignedToFilterOptions,
    activityFilterOptions,
    filtersVisible,
    setFiltersVisible,
    filteredSortedTasks: baseFilteredTasks,
    categoryFilters,
    setCategoryFilters
  } = useTaskFilters(sortedTasks, contacts, eventTypes);

  const filteredSortedTasks = hideCompleted
    ? baseFilteredTasks.filter((task) => {
        const status = task.currentOmitOption?.statusApplicableTo;
        return status !== 3 && status !== 4;
      })
    : baseFilteredTasks;

  const updateTaskStatus = async () => {
    await axios.post("/api/taskmanagement/newtasks/updateStatus", {
      basicTaskId: selectedTaskUploadDocument?.id,
      status: statusToUpdateAfterRequiredUpload
    });
    setStatusToUpdateAfterRequiredUpload(null);
  };

  const handleSelectTask = (taskId: number, isChecked: boolean): void => {
    setSelectedTasks((prevSelectedTasks) => {
      if (isChecked) {
        return { ...prevSelectedTasks, [taskId]: true };
      }
      const { [taskId]: _, ...remainingIds } = prevSelectedTasks;
      return remainingIds;
    });
  };

  const handleSelectAll = (tasks: NewTask[], isChecked: boolean): void => {
    if (isChecked) {
      const allTasksSelectedObj = tasks.reduce((builder, task) => {
        return { ...builder, [task.id]: true };
      }, {});
      setSelectedTasks(allTasksSelectedObj);
      return;
    }
    setSelectedTasks({});
  };

  const openUploadDocument = (task: NewTask) => {
    setSelectedTaskViewDocuments(null);
    setSelectedTaskUploadDocument(task);
  };

  const handleCopy = async (tasks: NewTask[]) => {
    var hasSelectedTasks = Object.keys(selectedTasks).length > 0;
    const allSelectedTasksId = hasSelectedTasks
      ? Object.keys(selectedTasks).filter((key) => selectedTasks[key])
      : tasks.map((t) => t.id.toString());
    const tasksToCopy = tasks.filter((t) => allSelectedTasksId.includes(String(t.id)));

    if (tasksToCopy.length === 0) {
      enqueueSnackbar("No tasks were selected. Please select tasks and try again", {
        variant: "error",
        autoHideDuration: 3500,
        anchorOrigin: { vertical: "bottom", horizontal: "center" }
      });
      return;
    }

    const success = await copyTasksToClipboard(tasksToCopy);

    if (success) {
      enqueueSnackbar("Selected tasks were copied to clipboard. You can now paste it into an email.", {
        variant: "success",
        autoHideDuration: 3500,
        anchorOrigin: { vertical: "bottom", horizontal: "center" }
      });
    } else {
      enqueueSnackbar("There was a problem copying the data. Please try again", {
        variant: "error",
        autoHideDuration: 3500,
        anchorOrigin: { vertical: "bottom", horizontal: "center" }
      });
    }
  };

  const isFlips = tasks[0].applicationId === 1;

  const handleOnDragEnd = async (result: any) => {
    if (!result.destination) {
      return;
    }

    const reorderedTasks = [...filteredSortedTasks];
    const [movedTask] = reorderedTasks.splice(result.source.index, 1);
    reorderedTasks.splice(result.destination.index, 0, movedTask);

    reorderedTasks.forEach((r, i) => (r.sortIndex = i));

    try {
      setTasks(reorderedTasks);
      const sortIndexes = reorderedTasks.map((r) => ({
        id: r.id,
        sortIndex: r.sortIndex
      }));
      await axios.post("/api/taskmanagement/newtasks/updateSortIndexes", { sortIndexes });
      enqueueSnackbar("Task order updated successfully.", { variant: "success" });
      refreshTasks();
    } catch (error) {
      enqueueSnackbar("Failed to update task order. Please try again.", { variant: "error" });
    }
  };

  // This useEffect ensures that the Autocomplete dropdowns close when clicking outside of them.
  // It listens for mousedown events on the document and triggers a blur on any open Autocomplete input.
  // The issue is caused by the drag and drop functionality, which interferes with the natural DOM blur events.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const autocompleteElements = document.querySelectorAll(".MuiAutocomplete-popper");
      autocompleteElements.forEach((autocompleteElement) => {
        if (!autocompleteElement.contains(event.target as Node)) {
          const openAutocomplete = document.querySelector(".MuiAutocomplete-root input:focus") as HTMLInputElement;
          if (openAutocomplete) {
            openAutocomplete.blur();
          }
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const syncHiddenColumns = () => {
      const stored = localStorage.getItem("hiddenColumns");
      setHiddenColumns(stored ? JSON.parse(stored) : []);
    };

    window.addEventListener("hiddenColumnsUpdated", syncHiddenColumns);
    return () => window.removeEventListener("hiddenColumnsUpdated", syncHiddenColumns);
  }, []);

  return (
    <>
      <TableContainer
        component={Card}
        sx={{
          maxHeight: isCondensed ? "calc(100vh - 105px)" : "calc(100vh - 424px)",
          height: "100%",
          border: "1px solid #F4F6F8",
          overflowX: "auto",
          ".MuiTableContainer-root": {
            maxHeight: isCondensed ? "calc(100vh - 105px)" : "calc(100vh - 424px)",
            height: "100%"
          },
          ".MuiTableCell-root": {
            padding: "8px 16px",
            verticalAlign: "middle"
          },
          ".MuiInputBase-root": {
            fontSize: { sm: "0.75rem", lg: "0.875rem" },
            minHeight: "32px"
          },
          ".MuiTableCell-head": {
            paddingTop: "0px",
            paddingBottom: "0px",
            color: "#000",
            fontSize: { sm: "0.75rem", lg: "0.875rem" }
          },
          "@media (max-width: 1199px)": {
            // Make all inputs smaller
            "& .MuiInputBase-root": {
              minHeight: "28px !important",
              fontSize: "0.75rem !important",
              "& .MuiInputBase-input": {
                padding: "4px 8px !important",
                fontSize: "0.75rem !important"
              }
            },
            // Make autocomplete dropdowns smaller
            "& .MuiAutocomplete-root .MuiInputBase-root": {
              minHeight: "28px !important",
              "& .MuiInputBase-input": {
                padding: "4px 8px !important"
              }
            },
            // Make select dropdowns smaller
            "& .MuiSelect-select": {
              padding: "4px 8px !important",
              fontSize: "0.75rem !important",
              minHeight: "20px !important"
            },
            // Reduce label size
            "& .MuiInputLabel-root": {
              fontSize: "0.7rem !important",
              transform: "translate(8px, 6px) scale(1)"
            },
            // Adjust label when focused/filled
            "& .MuiInputLabel-shrink": {
              transform: "translate(8px, -9px) scale(0.75) !important"
            },
            // Make textfield containers smaller
            "& .MuiTextField-root": {
              "& .MuiInputBase-root": {
                minHeight: "28px !important"
              }
            }
          }
        }}
      >
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="droppable" type="TASK">
            {(provided) => (
              <StickyTable ref={provided.innerRef} {...provided.droppableProps}>
                <TableHead>
                  <TableRow>
                    {!isFlips && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={filteredSortedTasks.every((task) => selectedTasks[task.id])}
                          onChange={(event) => handleSelectAll(filteredSortedTasks, event.target.checked)}
                          size="small"
                        />
                      </TableCell>
                    )}
                    {(
                      columns as {
                        key: string;
                        label: string;
                        accessor: (task: NewTask) => string | Date | undefined;
                      }[]
                    )
                      ?.filter((c) => !hiddenColumns.includes(c.key))
                      .map((column) => (
                        <TableCell
                          key={column.key}
                          style={{
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            padding: "0 16px"
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <TableSortLabel
                              active={sortConfig?.key === column.key}
                              direction={sortConfig?.key === column?.key ? sortConfig?.direction : "asc"}
                              onClick={() => {
                                const isAsc = sortConfig?.key === column?.key && sortConfig?.direction === "asc";
                                setSortConfig({
                                  key: column.key,
                                  direction: isAsc ? "desc" : "asc",
                                  accessor: column.accessor
                                });
                              }}
                            >
                              {column.label}
                            </TableSortLabel>
                          </Stack>
                        </TableCell>
                      ))}
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                        sx={{
                          padding: { xs: "2px 8px !important", lg: "2px 16px" },
                          "@media (max-width: 1199px)": {
                            position: "absolute",
                            right: 0,
                            top: "0"
                          },

                          "& .MuiIconButton-root": {
                            padding: {
                              xs: "4px",
                              sm: "8px"
                            },
                            borderRadius: "50%",
                            "@media (max-width: 1199px)": {
                              flexShrink: 0,
                              flex: "0 0 32px",
                              height: "32px",
                              width: "32px"
                            }
                          },
                          svg: {
                            fontSize: { xs: ".85rem", lg: "2rem" }
                          }
                        }}
                      >
                        <Tooltip title="Show Filters">
                          <IconButton onClick={() => setFiltersVisible(!filtersVisible)}>
                            <FilterListIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Hide Columns">
                          <IconButton
                            onClick={(e) => setViewComlumnAnchorEl(e.currentTarget)}
                            color={hiddenColumns.length > 0 ? "primary" : "default"}
                          >
                            <ViewColumnIcon />
                          </IconButton>
                        </Tooltip>

                        <HideColumns
                          open={Boolean(viewComlumnAnchorEl)}
                          handleClose={() => setViewComlumnAnchorEl(null)}
                          anchorEl={viewComlumnAnchorEl}
                        />

                        <Tooltip title="Copy marked for Emailing">
                          <IconButton onClick={() => handleCopy(filteredSortedTasks)}>
                            <Iconify icon="fluent:mail-list-28-regular" />
                          </IconButton>
                        </Tooltip>
                        {!isFlips && (
                          <Stack direction="column" justifyContent="center">
                            <Box width={100}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  if (Object.keys(selectedTasks).length < 1) {
                                    enqueueSnackbar("Mark the checkboxes for the tasks you’d like to bulk update.", {
                                      variant: "error",
                                      autoHideDuration: 3500,
                                      anchorOrigin: { vertical: "top", horizontal: "center" }
                                    });
                                    return;
                                  }
                                  setIsMultiStatusModalOpen(true);
                                }}
                                fullWidth
                              >
                                Bulk Update
                              </Button>
                            </Box>
                          </Stack>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TaskFilters
                    chosenStatusCategories={statusFilters}
                    setChosenStatusCategories={setStatusFilters}
                    statusCategories={statusCategories}
                    activityFilters={activityFilters}
                    setActivityFilters={setActivityFilters}
                    assignedToFilters={assignedToFilters}
                    setAssignedToFilters={setAssignedToFilters}
                    associationFilters={associationFilters}
                    setAssociationFilters={setAssociationFilters}
                    taskFilter={taskFilter}
                    setTaskFilter={setTaskFilter}
                    taskQuickNoteFilter={taskQuickNoteFilter}
                    setTaskQuickNoteFilter={setTaskQuickNoteFilter}
                    associationFilterOptions={associationFilterOptions}
                    assignedToFilterOptions={assignedToFilterOptions}
                    activityFilterOptions={activityFilterOptions}
                    filtersVisible={filtersVisible}
                    setFiltersVisible={setFiltersVisible}
                    columns={columns}
                    eventTypes={eventTypes}
                    applicationId={tasks[0]?.applicationId}
                    categories={categories}
                    chosenCategories={categoryFilters}
                    setChosenCategories={setCategoryFilters}
                    hiddenColumns={hiddenColumns}
                  />
                </TableHead>
                <TableBody>
                  {filteredSortedTasks.map((task, index) => (
                    <Draggable key={task.id} draggableId={task.id.toString()} index={index}>
                      {(provided, snapshot) => (
                        <NewTaskRow
                          t={task}
                          eventTypes={eventTypes}
                          refreshTasks={refreshTasks}
                          selectedTaskNotes={selectedTaskNotes}
                          selectedTaskAssignment={selectedTaskAssignment}
                          setSelectedAssignment={setSelectedTaskAssignment}
                          setSelectedTaskNotes={setSelectedTaskNotes}
                          selectedTaskUploadDocument={selectedTaskUploadDocument}
                          setSelectedTaskUploadDocument={setSelectedTaskUploadDocument}
                          selectedTaskViewDocuments={selectedTaskViewDocuments}
                          setSelectedTaskViewDocuments={setSelectedTaskViewDocuments}
                          setSelectedTaskHistory={setSelectedTaskHistory}
                          contacts={contacts}
                          handleSelectTask={handleSelectTask}
                          isSelected={Boolean(selectedTasks[task.id])}
                          selectedTasks={selectedTasks}
                          index={index}
                          provided={provided}
                          snapshot={snapshot}
                          categories={categories}
                          rowStyle={{
                            backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa"
                          }}
                          hiddenColumns={hiddenColumns}
                          setStatusToUpdateAfterRequiredUpload={setStatusToUpdateAfterRequiredUpload}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  <TableRow>
                    <TableCell colSpan={100} sx={{ height: "48px", border: 0 }} />
                  </TableRow>
                </TableBody>
              </StickyTable>
            )}
          </Droppable>
        </DragDropContext>
      </TableContainer>
      {selectedTaskNotes && (
        <TaskNotesModal
          selectedTask={selectedTaskNotes}
          setSelectedTask={setSelectedTaskNotes}
          updateTasks={refreshTasks}
        />
      )}
      {selectedTaskAssignment && (
        <AssignmentModal
          selectedTask={selectedTaskAssignment}
          setSelectedTask={setSelectedTaskAssignment}
          updateTasks={refreshTasks}
        />
      )}
      {selectedTaskUploadDocument && (
        <DocumentUploadModal
          selectedTask={selectedTaskUploadDocument}
          setSelectedTask={setSelectedTaskUploadDocument}
          updateTasks={refreshTasks}
          orderNumber={orderNumber}
          updateTaskStatus={updateTaskStatus}
          doUpdateTaskAfterDocUpload={!!statusToUpdateAfterRequiredUpload}
        />
      )}
      {selectedTaskViewDocuments && (
        <ViewAssociatedDocumentsModal
          selectedTask={selectedTaskViewDocuments}
          setSelectedTask={setSelectedTaskViewDocuments}
          orderNumber={orderNumber}
          uploadDocument={openUploadDocument}
        />
      )}
      <MultiStatusUpdateModal
        open={isMultiStatusModalOpen}
        onClose={() => setIsMultiStatusModalOpen(false)}
        selectedTasks={selectedTasks}
        refreshTasks={refreshTasks}
        onUpdated={() => setSelectedTasks({})}
        tasks={tasks}
      />
    </>
  );
};

export default TasksTable;
